import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info, RotateCcw, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/api";
import { INSTRUMENTS } from "@/lib/library";

function ItemRow({ r, s, canEdit, onChange }) {
  const edits = s.item_edits || {};
  const removed = (s.removed_items || []).includes(r.id);
  const setText = (v) => {
    const next = { ...edits };
    if (!v || v === r.text) delete next[r.id]; else next[r.id] = v;
    onChange({ ...s, item_edits: next });
  };
  const toggle = () => onChange({ ...s, removed_items: removed ? s.removed_items.filter((x) => x !== r.id) : [...(s.removed_items || []), r.id] });
  return (
    <li className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`personality-item-${r.id}`}>
      {removed ? <span className="flex-1 text-ink-muted line-through">{edits[r.id] || r.text}</span>
        : <Input value={edits[r.id] ?? r.text} disabled={!canEdit} onChange={(e) => setText(e.target.value)} className="h-8 flex-1 text-sm" aria-label="Statement text" data-testid={`personality-item-input-${r.id}`} />}
      <span className="shrink-0 rounded-full bg-subtle px-2 py-0.5 text-[11px] text-ink-muted">{r.trait}</span>
      {canEdit && <Button size="icon" variant="ghost" onClick={toggle} aria-label={removed ? "Restore statement" : "Remove statement"} data-testid={`personality-item-${removed ? "restore" : "remove"}-${r.id}`}>
        {removed ? <RotateCcw size={14} /> : <Trash2 size={14} />}</Button>}
    </li>
  );
}

export function PersonalityEditor({ s, onChange, canEdit }) {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["personality-items", s.instrument], queryFn: () => get(`/assessment-library/personality/${s.instrument}`), enabled: !!s.instrument });
  const rows = data?.items || [];
  const edits = s.item_edits || {};
  const shown = q ? rows.filter((r) => `${edits[r.id] || r.text} ${r.trait}`.toLowerCase().includes(q.toLowerCase())) : rows;
  const removed = (s.removed_items || []).length;
  return (
    <div className="space-y-3" data-testid="personality-editor">
      <div className="flex items-start gap-3 rounded-[6px] border border-line bg-subtle p-4" data-testid="personality-edit-note" data-tour="locked-questions">
        <Info size={18} className="mt-0.5 shrink-0 text-ink-muted" aria-hidden />
        <div className="space-y-1 text-sm">
          <p className="font-medium">Every statement can be edited or removed</p>
          <p className="text-ink-secondary">{data?.note} Statements are shown in a random order to each candidate. Rewording can make scores less comparable with the research norms.</p>
          {data?.validity && <p className="text-xs text-ink-muted">{data.validity}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="space-y-1 text-sm"><span className="text-ink-secondary">Version</span>
          <select value={s.instrument} disabled={!canEdit} onChange={(e) => onChange({ ...s, instrument: e.target.value, item_edits: {}, removed_items: [] })} className="block h-9 rounded-[5px] border border-line bg-white px-2" data-testid="personality-instrument">
            {Object.entries(INSTRUMENTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <span className="text-xs text-ink-muted" data-testid="personality-counts">{rows.length - removed} of {rows.length} statements · {Object.keys(edits).length} edited</span>
        <div className="relative ml-auto w-full max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${rows.length} statements`} className="pl-9" aria-label="Search statements" data-testid="personality-search" />
        </div>
      </div>
      <ol className="max-h-96 divide-y divide-line overflow-y-auto rounded-[6px] border border-line bg-white" aria-label="Personality statements" data-testid="personality-items">
        {shown.map((r) => <ItemRow key={r.id} r={r} s={s} canEdit={canEdit} onChange={onChange} />)}
        {!shown.length && <li className="px-4 py-3 text-sm text-ink-muted">No statements match "{q}".</li>}
      </ol>
    </div>
  );
}
